import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Terminal, 
  Code, 
  Database, 
  Cloud,
  Lock,
  Zap,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import i18n from '../../i18n';

const WebProgramming = () => {
  const { t, ready } = useTranslation();

  // Force reload translations when component mounts
  useEffect(() => {
    console.log('WebProgramming component mounted, reloading translations');
    i18n.reloadResources().then(() => {
      console.log('Translations successfully reloaded in WebProgramming');
      // Debug translation values
      console.log('Current language:', i18n.language);
      console.log('Title translation:', t('services.webProgramming.title'));
      console.log('Description translation:', t('services.webProgramming.description'));
      
      // Debugging more information
      console.log('DEBUG - WebProgramming - All translation values:');
      console.log('services object:', i18n.t('services', { returnObjects: true }));
      console.log('services.webProgramming object:', i18n.t('services.webProgramming', { returnObjects: true }));
    }).catch(err => {
      console.error('Failed to reload translations in WebProgramming:', err);
    });
  }, [t]);

  const features = [
    {
      icon: Code,
      title: t('services.webProgramming.features.customProgramming.title'),
      description: t('services.webProgramming.features.customProgramming.description')
    },
    {
      icon: Database,
      title: t('services.webProgramming.features.backend.title'),
      description: t('services.webProgramming.features.backend.description')
    },
    {
      icon: Cloud,
      title: t('services.webProgramming.features.cloud.title'),
      description: t('services.webProgramming.features.cloud.description')
    },
    {
      icon: Lock,
      title: t('services.webProgramming.features.security.title'),
      description: t('services.webProgramming.features.security.description')
    },
    {
      icon: Zap,
      title: t('services.webProgramming.features.performance.title'),
      description: t('services.webProgramming.features.performance.description')
    }
  ];

  // Technologies structure
  const technologies = [
    {
      title: t('services.webProgramming.technologies.frontend.title'),
      items: [
        'React', 'Vue.js', 'Next.js', 'Angular', 'Svelte', 'TypeScript', 'JavaScript', 'HTML/CSS', 'TailwindCSS'
      ]
    },
    {
      title: t('services.webProgramming.technologies.backend.title'),
      items: [
        'Node.js', 'Express', 'NestJS', 'PHP', 'Laravel', 'Python', 'Django', 'FastAPI', 'Java', 'Spring Boot', 'C#', '.NET'
      ]
    },
    {
      title: t('services.webProgramming.technologies.database.title'),
      items: [
        'PostgreSQL', 'MySQL', 'MongoDB', 'Redis', 'Firebase', 'Supabase', 'DynamoDB', 'Elasticsearch'
      ]
    }
  ];

  // If translations are not ready, show loading state
  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center">Hleður...</div>;
  }

  return (
    <>
      <SEO 
        title={t('services.webProgramming.title')}
        description={t('services.webProgramming.description')}
        keywords={t('site.keywords')}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <motion.div 
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                {t('services.webProgramming.title')}
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                {t('services.webProgramming.description')}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Features Section */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2 
                className="text-3xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {t('services.webProgramming.featuresTitle')}
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t('services.webProgramming.featuresDescription')}
              </motion.p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-gray-50 rounded-xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                    <feature.icon className="w-6 h-6 text-[#EA295A]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Technologies Section */}
        <div className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2 
                className="text-3xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {t('services.webProgramming.technologiesTitle')}
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t('services.webProgramming.technologiesDescription')}
              </motion.p>
            </div>
            
            <div className="space-y-12">
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">{tech.title}</h3>
                  <div className="flex flex-wrap gap-3">
                    {tech.items.map((item, idx) => (
                      <div 
                        key={idx}
                        className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Development Process */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2 
                className="text-3xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {t('services.webProgramming.processTitle')}
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t('services.webProgramming.processDescription')}
              </motion.p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((step, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="bg-gray-50 rounded-xl p-8 h-full">
                    <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#EA295A]/10 mb-6">
                      <span className="text-[#EA295A] font-bold">{step}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {t(`services.webProgramming.process.step${step}.title`)}
                    </h3>
                    <p className="text-gray-600">
                      {t(`services.webProgramming.process.step${step}.description`)}
                    </p>
                  </div>
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <motion.h2 
                className="text-3xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {t('services.webProgramming.benefitsTitle')}
              </motion.h2>
              <motion.p 
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t('services.webProgramming.benefitsDescription')}
              </motion.p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {[1, 2, 3, 4, 5, 6].map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-start"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="w-6 h-6 text-[#EA295A]" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {t(`services.webProgramming.benefits.benefit${benefit}.title`)}
                    </h3>
                    <p className="text-gray-600">
                      {t(`services.webProgramming.benefits.benefit${benefit}.description`)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
              <motion.h2 
                className="text-3xl font-bold text-white mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {t('services.webProgramming.cta.title')}
              </motion.h2>
              <motion.p 
                className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                {t('services.webProgramming.cta.description')}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                  >
                    {t('common.getQuote')}
                  </motion.button>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WebProgramming;